import { Component, ojs, app, v, state } from "modular-openscriptjs";
import { monetary, formatThousand, either } from "../../../helpers";

const h = app("h");
const gc = app("gc");

export default class ServiceView extends Component {
    expanded = state(false);

    render(item, order, ...args) {
        const service = item.versioned_buyable;
        if (!service) return h.empty();

        const userCurrency = gc.user?.currency || "KES";
        const hasRequirements = item.requirements && item.requirements.length > 0;

        // Split args: first is status badge, second is action buttons, third is parent styling/attributes
        const statusBadge = args[0] || h.empty();
        const actionButtons = args[1] || h.empty();
        const cardAttributes = args[2] || {};

        // Merge default layout/flex classes with parent card attributes
        const defaultClasses = "position-relative d-flex flex-column gap-3";
        const mergedClass = `${cardAttributes.class || ""} ${defaultClasses}`.trim();
        const mergedAttributes = {
            ...cardAttributes,
            class: mergedClass
        };

        return h.div(
            mergedAttributes,
            // Status Badge at the top
            statusBadge,

            // First Container: Image on left, Name & Price on right (Enlarged)
            h.div(
                { class: "d-flex align-items-center gap-3" },
                // Image or Placeholder
                either(
                    service.thumbnail,
                    () => h.img({
                        src: service.thumbnail,
                        class: "rounded-3 object-fit-cover border flex-shrink-0",
                        style: "width: 80px; height: 80px;"
                    }),
                    () => h.div(
                        {
                            class: "bg-light rounded-3 d-flex align-items-center justify-content-center text-secondary border border-light-subtle flex-shrink-0",
                            style: "width: 80px; height: 80px;"
                        },
                        h.i({ class: "bi bi-tools fs-2" })
                    )
                ),

                // Name and Price (Enlarged fs-4)
                h.div(
                    { class: "flex-fill min-w-0" },
                    h.div(
                        { class: "d-flex gap-2 flex-column" },
                        h.h4({ class: "fw-bold text-dark mb-0 text-wrap fs-4" }, service.name),
                        h.div(
                            { class: "flex-shrink-0" },
                            h.div({ class: "fw-bold text-primary fs-4" }, monetary(item.price * item.quantity, userCurrency)),
                            either(item?.company_id, () => h.div(
                                { class: "text-muted fs-xsm" },
                                "Business Commission: ",
                                h.strong({ class: "text-dark" }, `${Math.ceil(item?.business_commission_rate ?? 0)}%`),
                                either(item?.business_commission > 0, () => h.span(
                                    ` (${monetary(item.business_commission, userCurrency)})`
                                ))
                            ))
                        )
                    )
                )
            ),

            // Second Container: Quantity and shortened Description with Read More (Enlarged)
            h.div(
                { class: "d-flex flex-column gap-2 bg-light-subtle p-3 rounded-3 border border-light-subtle" },
                h.div({ class: "text-primary fw-bold fs-4 d-flex align-items-center gap-2" }, 
                    h.i({ class: "bi bi-cart-fill fs-4" }), 
                    `Quantity: ${item.quantity}`
                ),
                this.renderDescription(service.description)
            ),

            // Requirements (Parsed and Styled Prominently)
            either(hasRequirements, () => this.renderRequirements(item.requirements, userCurrency)),

            // Instructions Row
            h.div(
                { class: "row g-2" },
                either(service.customer_instruction, () => h.div(
                    { class: "col-12 col-md-6" },
                    h.div(
                        { class: "p-3 bg-light rounded-3 border border-light-subtle h-100" },
                        h.small({ class: "text-secondary fw-bold d-block fs-xxs text-uppercase mb-1" }, "Customer Instruction:"),
                        h.div({ class: "text-dark fs-xsm" }, service.customer_instruction)
                    )
                )),
                either(service.deliverer_instruction, () => h.div(
                    { class: "col-12 col-md-6" },
                    h.div(
                        { class: "p-3 bg-light rounded-3 border border-light-subtle h-100" },
                        h.small({ class: "text-secondary fw-bold d-block fs-xxs text-uppercase mb-1" }, "Deliverer Instruction:"),
                        h.div({ class: "text-dark fs-xsm" }, service.deliverer_instruction)
                    )
                ))
            ),

            // Action Buttons at the bottom
            actionButtons
        );
    }

    renderRequirements(requirements, userCurrency) {
        if (!requirements || requirements.length === 0) return h.empty();

        // Categorize requirements based on values to parse them beautifully
        const files = [];
        const booleans = [];
        const choices = [];

        requirements.forEach(req => {
            const val = String(req.requirement_value).trim();
            const isUrl = val.startsWith('http://') || val.startsWith('https://');
            const hasFileExtension = /\.(pdf|jpg|jpeg|png|gif|zip|doc|docx|xls|xlsx|txt)$/i.test(val);
            
            if (isUrl || hasFileExtension) {
                files.push(req);
            } else if (['yes', 'no', 'true', 'false'].includes(val.toLowerCase())) {
                booleans.push(req);
            } else {
                choices.push(req);
            }
        });

        return h.div(
            { class: "d-flex flex-column gap-3" },
            // 1. Standard choices (styled as badges/features, enlarged font fs-5)
            either(choices.length > 0, () => h.div(
                { class: "d-flex flex-column gap-2" },
                h.small({ class: "text-secondary fw-bold fs-xsm text-uppercase" }, "Options:"),
                h.div(
                    { class: "d-flex flex-wrap gap-2" },
                    choices.map(req => h.span(
                        { class: "badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle rounded-3 fs-5 fw-semibold py-2.5 px-3.5" },
                        `${req.requirement_name}: `,
                        h.strong({ class: "text-dark fw-bold" }, req.requirement_value),
                        either(req.requirement_price > 0, ` (+${monetary(req.requirement_price, userCurrency)})`)
                    ))
                )
            )),

            // 2. Booleans / Yes-No options (styled under high-end "Included Extras/Add-ons" highlight)
            either(booleans.length > 0, () => h.div(
                { class: "text-dark bg-success-subtle p-3 rounded-3 border border-success-subtle d-flex flex-column gap-2" },
                h.div({ class: "fw-bold text-success d-flex align-items-center gap-2 fs-5" }, 
                    h.i({ class: "bi bi-check-circle-fill fs-5" }), 
                    "Included Add-ons:"
                ),
                h.div(
                    { class: "d-flex flex-wrap gap-2" },
                    booleans.map(req => {
                        const val = String(req.requirement_value).toLowerCase();
                        const isPositive = val === 'yes' || val === 'true';
                        return h.span(
                            { class: `badge rounded-3 fs-5 fw-semibold py-2.5 px-3.5 ${isPositive ? 'bg-white text-success border border-success' : 'bg-white text-secondary border border-secondary-subtle'}` },
                            `${req.requirement_name}: ${isPositive ? 'Yes' : 'No'}`,
                            either(req.requirement_price > 0 && isPositive, ` (+${monetary(req.requirement_price, userCurrency)})`)
                        );
                    })
                )
            )),

            // 3. File uploads (styled as beautiful download cards)
            either(files.length > 0, () => h.div(
                { class: "d-flex flex-column gap-2" },
                h.small({ class: "text-secondary fw-bold fs-xsm text-uppercase" }, "Uploaded Files:"),
                h.div(
                    { class: "row g-2" },
                    files.map(req => {
                        const url = req.requirement_value;
                        const fileName = url.substring(url.lastIndexOf('/') + 1) || req.requirement_name;
                        return h.div(
                            { class: "col-12 col-md-6" },
                            h.div(
                                { class: "p-3 bg-light rounded-3 border border-light-subtle d-flex align-items-center justify-content-between gap-3 h-100" },
                                h.div(
                                    { class: "d-flex align-items-center gap-2 min-w-0" },
                                    h.i({ class: "bi bi-file-earmark-arrow-down-fill text-primary fs-4" }),
                                    h.div(
                                        { class: "min-w-0" },
                                        h.div({ class: "fw-bold text-dark fs-xsm text-truncate" }, req.requirement_name),
                                        h.small({ class: "text-muted d-block text-truncate" }, fileName)
                                    )
                                ),
                                h.a(
                                    {
                                        href: url,
                                        target: "_blank",
                                        class: "btn btn-sm btn-primary rounded-pill px-3 fw-bold flex-shrink-0"
                                    },
                                    "Download"
                                )
                            )
                        );
                    })
                )
            ))
        );
    }

    renderDescription(desc) {
        if (!desc) return h.empty();
        
        const limit = 80;
        if (desc.length <= limit) {
            return h.div({ class: "text-muted fs-5" }, desc);
        }

        return v(this.expanded, (isExpanded) => {
            if (isExpanded.value) {
                return h.div(
                    { class: "text-muted fs-5" },
                    desc,
                    " ",
                    h.a({
                        href: "#",
                        class: "text-primary fw-bold text-decoration-none ms-1",
                        listeners: {
                            click: (e) => {
                                e.preventDefault();
                                this.expanded.value = false;
                            }
                        }
                    }, "Read less")
                );
            } else {
                return h.div(
                    { class: "text-muted fs-5" },
                    desc.substring(0, limit) + "...",
                    " ",
                    h.a({
                        href: "#",
                        class: "text-primary fw-bold text-decoration-none ms-1",
                        listeners: {
                            click: (e) => {
                                e.preventDefault();
                                this.expanded.value = true;
                            }
                        }
                    }, "Read more")
                );
            }
        });
    }
}

ojs(ServiceView);
